/**
 * backgroundFx — camera background blur / virtual-background for 1:1 calls.
 *
 * SAFETY CONTRACT (why this is additive, never call-breaking):
 *   - The DEFAULT mode is 'none'. With 'none' the caller publishes the RAW
 *     getUserMedia track untouched — byte-identical to the pre-feature path. We
 *     only build a processor when the user explicitly picks an effect.
 *   - EVERYTHING degrades to the raw camera. If the segmentation runtime / model
 *     can't load (old webview, no WASM SIMD, offline, file missing), or the
 *     browser lacks `canvas.captureStream`, `createBackgroundProcessor` returns
 *     `null` and the caller keeps using the raw camera. A failed effect never
 *     drops the call.
 *
 * HOW IT WORKS
 *   The selfie-segmentation model (MediaPipe Tasks-Vision `ImageSegmenter`) runs
 *   per-frame on the local camera, producing a person/background confidence
 *   mask. We composite onto a hidden <canvas> — a blurred copy of the frame, or
 *   a chosen background image, with the masked person drawn on top — and publish
 *   `canvas.captureStream()` instead of the raw camera. The peer sees the effect;
 *   so does your self-preview (the caller binds the same processed stream).
 *
 * ASSETS ARE SELF-HOSTED (same-origin under `public/mediapipe/` +
 * `public/backgrounds/`). That keeps the existing app CSP intact —
 * `script-src 'self' 'wasm-unsafe-eval'` + `worker-src 'self' blob:` +
 * `connect-src 'self'` already cover a same-origin WASM module, so NO CSP edit
 * (and no desktop rebuild just for CSP) is required. See docs/calls.md.
 */

const BASE_URL: string = import.meta.env.BASE_URL || '/';
const STORAGE_KEY = 'xop.call.backgroundMode';

/** `'none'` (raw camera) · `'blur'` (blurred background) · a preset id. */
export type BackgroundMode = 'none' | 'blur' | (string & {});

export interface BackgroundPreset {
  id: string;
  label: string;
  /** Same-origin image URL drawn behind the segmented person. */
  url: string;
}

/** Curated, same-origin backgrounds (SVG → rasterised into the canvas). */
export const BACKGROUND_PRESETS: BackgroundPreset[] = [
  { id: 'aurora', label: 'Aurora', url: `${BASE_URL}backgrounds/aurora.svg` },
  { id: 'sky', label: 'Sky', url: `${BASE_URL}backgrounds/sky.svg` },
  { id: 'warm', label: 'Warm', url: `${BASE_URL}backgrounds/warm.svg` },
  { id: 'slate', label: 'Slate', url: `${BASE_URL}backgrounds/slate.svg` },
];

/** True for a mode that is NOT the raw camera (i.e. needs a processor). */
export function isEffectMode(mode: BackgroundMode): boolean {
  return mode !== 'none';
}

/** A mode the app knows about — guards stored prefs against stale/garbage ids. */
export function isValidBackgroundMode(value: string): value is BackgroundMode {
  return value === 'none' || value === 'blur' || BACKGROUND_PRESETS.some((p) => p.id === value);
}

/** Resolve a preset id → its image URL (null for 'none' / 'blur' / unknown). */
export function resolveBackgroundUrl(mode: BackgroundMode): string | null {
  return BACKGROUND_PRESETS.find((p) => p.id === mode)?.url ?? null;
}

/** Read the persisted choice (defaults to 'none' on any read error). */
export function loadBackgroundPref(): BackgroundMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && isValidBackgroundMode(v)) return v;
  } catch {
    /* localStorage blocked (private mode / webview) — fall through */
  }
  return 'none';
}

/** Persist the choice so the next call remembers it. */
export function saveBackgroundPref(mode: BackgroundMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* non-fatal */
  }
}

/**
 * Best-effort, synchronous capability gate for showing the UI. The authoritative
 * check is `createBackgroundProcessor` returning non-null (it actually loads the
 * model). This just hides the picker where it can't possibly work.
 */
export function backgroundFxSupported(): boolean {
  return (
    typeof document !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.captureStream === 'function'
  );
}

export interface BackgroundProcessor {
  /** The processed video stream to publish + self-preview. */
  readonly stream: MediaStream;
  /** Switch effect live (blur ↔ a preset) without rebuilding the segmenter. */
  setMode(mode: BackgroundMode): Promise<void>;
  /** Repoint at a new raw camera (used when the user switches camera device). */
  setSource(source: MediaStream): void;
  /** Tear down the loop + segmenter + output track. Does NOT stop `source`. */
  stop(): void;
}

// --- Minimal structural types for the dynamically-imported MediaPipe bundle ---
interface MpMask {
  getAsFloat32Array(): Float32Array;
  readonly width: number;
  readonly height: number;
}
interface MpResult {
  confidenceMasks?: MpMask[];
}
interface MpSegmenter {
  segmentForVideo(video: HTMLVideoElement, timestampMs: number, cb: (r: MpResult) => void): void;
  close(): void;
}
interface MpModule {
  FilesetResolver: { forVisionTasks(wasmPath: string): Promise<unknown> };
  ImageSegmenter: { createFromOptions(fileset: unknown, opts: unknown): Promise<MpSegmenter> };
}

let mpModulePromise: Promise<MpModule> | null = null;
function loadMediaPipe(): Promise<MpModule> {
  if (!mpModulePromise) {
    // @vite-ignore keeps Vite from trying to bundle/analyze the same-origin
    // public asset; it loads at runtime only when a user opts into an effect.
    mpModulePromise = import(/* @vite-ignore */ `${BASE_URL}mediapipe/vision_bundle.js`) as Promise<MpModule>;
  }
  return mpModulePromise;
}

const imageCache = new Map<string, HTMLImageElement>();
function loadImage(url: string): Promise<HTMLImageElement | null> {
  const cached = imageCache.get(url);
  if (cached) return Promise.resolve(cached);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      imageCache.set(url, img);
      resolve(img);
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Draw `img` over the whole canvas with object-fit: cover. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  w: number,
  h: number,
): void {
  const iw = img.naturalWidth || img.width || w;
  const ih = img.naturalHeight || img.height || h;
  const scale = Math.max(w / iw, h / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
}

/**
 * Build a background processor over `source` (a video-only camera stream).
 * Returns `null` if effects aren't supported here or the model fails to load —
 * the caller MUST fall back to the raw camera in that case.
 */
export async function createBackgroundProcessor(
  source: MediaStream,
  initialMode: BackgroundMode,
  opts?: { fps?: number; blurPx?: number; maxWidth?: number },
): Promise<BackgroundProcessor | null> {
  if (!backgroundFxSupported()) return null;

  const fps = opts?.fps ?? 24;
  const blurPx = opts?.blurPx ?? 14;
  const maxWidth = opts?.maxWidth ?? 640;

  let segmenter: MpSegmenter;
  try {
    const mp = await loadMediaPipe();
    const fileset = await mp.FilesetResolver.forVisionTasks(`${BASE_URL}mediapipe/wasm`);
    segmenter = await mp.ImageSegmenter.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: `${BASE_URL}mediapipe/selfie_segmenter.tflite` },
      runningMode: 'VIDEO',
      outputConfidenceMasks: true,
      outputCategoryMask: false,
    });
  } catch {
    return null; // WASM/model unavailable → caller uses raw camera
  }

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.srcObject = source;
  void video.play().catch(() => { /* will start once frames arrive */ });

  const out = document.createElement('canvas');
  const person = document.createElement('canvas');
  const mask = document.createElement('canvas');
  const outCtx = out.getContext('2d');
  const personCtx = person.getContext('2d');
  const maskCtx = mask.getContext('2d', { willReadFrequently: true });
  if (!outCtx || !personCtx || !maskCtx) {
    try { segmenter.close(); } catch { /* ignore */ }
    return null;
  }

  // Seed a size from the track so captureStream emits frames immediately.
  const seed = source.getVideoTracks()[0]?.getSettings?.() ?? {};
  const seedW = Math.min(maxWidth, seed.width ?? 640);
  const seedH = seed.width && seed.height ? Math.round(seedW * (seed.height / seed.width)) : Math.round((seedW * 3) / 4);
  out.width = seedW;
  out.height = seedH;
  person.width = seedW;
  person.height = seedH;
  outCtx.fillStyle = '#000';
  outCtx.fillRect(0, 0, seedW, seedH);

  let mode: BackgroundMode = initialMode;
  let bgImage: HTMLImageElement | null = null;
  const presetUrl = resolveBackgroundUrl(initialMode);
  if (presetUrl) bgImage = await loadImage(presetUrl);

  let stopped = false;
  let rafId = 0;
  let lastDraw = 0;
  let lastTs = -1;
  const minDelta = 1000 / fps;

  const ensureSize = (vw: number, vh: number): boolean => {
    let w = vw;
    let h = vh;
    if (w > maxWidth) {
      h = Math.round((h * maxWidth) / w);
      w = maxWidth;
    }
    if (w > 0 && h > 0 && (out.width !== w || out.height !== h)) {
      out.width = w;
      out.height = h;
      person.width = w;
      person.height = h;
      return true; // the canvas was cleared by the resize
    }
    return false;
  };

  const drawRaw = (): void => {
    outCtx.drawImage(video, 0, 0, out.width, out.height);
  };

  const composite = (result: MpResult): void => {
    const w = out.width;
    const h = out.height;

    // 1) Background layer.
    if (mode === 'blur') {
      outCtx.save();
      outCtx.filter = `blur(${blurPx}px)`;
      outCtx.drawImage(video, 0, 0, w, h);
      outCtx.restore();
    } else if (bgImage) {
      drawCover(outCtx, bgImage, w, h);
    } else {
      drawRaw();
      return; // unknown/none → passthrough
    }

    // 2) Person/background confidence mask → an alpha bitmap.
    const m = result.confidenceMasks?.[0];
    if (!m) {
      drawRaw(); // no mask this frame → show the raw camera rather than a void
      return;
    }
    const mw = m.width;
    const mh = m.height;
    const conf = m.getAsFloat32Array();
    mask.width = mw;
    mask.height = mh;
    const id = maskCtx.createImageData(mw, mh);
    for (let i = 0; i < conf.length; i++) {
      id.data[i * 4 + 3] = Math.round((conf[i] ?? 0) * 255);
    }
    maskCtx.putImageData(id, 0, 0);

    // 3) Person = raw frame keyed by the (slightly softened) mask alpha.
    personCtx.clearRect(0, 0, w, h);
    personCtx.drawImage(video, 0, 0, w, h);
    personCtx.save();
    personCtx.globalCompositeOperation = 'destination-in';
    personCtx.filter = 'blur(2px)'; // soften the cut-out edge
    personCtx.drawImage(mask, 0, 0, w, h);
    personCtx.restore();

    // 4) Person over the background.
    outCtx.drawImage(person, 0, 0, w, h);
  };

  const tick = (): void => {
    if (stopped) return;
    rafId = requestAnimationFrame(tick);
    const now = performance.now();
    if (now - lastDraw < minDelta) return;
    lastDraw = now;
    if (video.readyState < 2 || video.videoWidth === 0) return;
    // Resizing a canvas clears it; redraw the raw frame immediately so the
    // captureStream compositor never samples a blank canvas before the (async)
    // segmentation callback composites this frame.
    if (ensureSize(video.videoWidth, video.videoHeight)) drawRaw();

    if (mode === 'none') {
      drawRaw();
      return;
    }
    // VIDEO mode requires strictly increasing timestamps.
    let ts = now;
    if (ts <= lastTs) ts = lastTs + 1;
    lastTs = ts;
    try {
      segmenter.segmentForVideo(video, ts, (result) => {
        try {
          composite(result);
        } catch {
          drawRaw();
        }
      });
    } catch {
      drawRaw();
    }
  };

  let stream: MediaStream;
  try {
    stream = out.captureStream(fps);
  } catch {
    // captureStream can throw on some webviews / under resource pressure. Honour
    // the safety contract: return null so the caller publishes the raw camera.
    try { segmenter.close(); } catch { /* ignore */ }
    return null;
  }
  rafId = requestAnimationFrame(tick);

  return {
    stream,
    async setMode(next: BackgroundMode): Promise<void> {
      mode = next;
      const url = resolveBackgroundUrl(next);
      bgImage = url ? await loadImage(url) : null;
    },
    setSource(next: MediaStream): void {
      video.srcObject = next;
      void video.play().catch(() => { /* ignore */ });
    },
    stop(): void {
      stopped = true;
      cancelAnimationFrame(rafId);
      try { segmenter.close(); } catch { /* ignore */ }
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      video.srcObject = null;
    },
  };
}
